import { useEffect, useRef } from 'react';

import { RTC_IFRAME_ID } from '../../../js/constants';

interface MiniSocketListenerProps {
  signalingUrl: string; // Signaling URL for the iframe
  debug?: boolean; // Enable debug mode
  onMessage: (data: unknown) => void; // Callback called when a matching event is received
  messageFilter: (event: MessageEvent) => boolean; // Function that filters message events (returns true for events to handle)
}

/**
 * Generic component to listen to events from an iframe.
 * Inserts an invisible iframe into the DOM (if one does not already exist) and listens to postMessage events.
 */
const MiniSocketListener = ({
  signalingUrl,
  debug = false,
  onMessage = () => {},
  messageFilter = () => true,
}: MiniSocketListenerProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // When RTC is not configured for the tenant/domain, the signaling URL is missing
  // (and a Jinja-rendered `None` arrives here as the literal string "None"). In that
  // case there is nothing to listen to: skip both effects rather than throwing on `new URL()`.
  const hasSignalingUrl = Boolean(signalingUrl) && signalingUrl !== 'None';

  // Set or reset the listener, using the given filter and callback
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!hasSignalingUrl) {
        return undefined;
      }
      if (event.origin !== signalingUrl) {
        return;
      }
      try {
        if (messageFilter(event)) {
          onMessage(event.data);
        }
      } catch (error) {
        console.error('Error during message handling:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onMessage, messageFilter, signalingUrl, hasSignalingUrl]);

  // Insert the hidden iframe in the DOM if it doesn't exist yet
  useEffect(() => {
    if (!hasSignalingUrl) {
      return;
    }

    if (document.querySelector(`#${RTC_IFRAME_ID}`)) {
      return;
    }

    const logLevel = debug ? '2' : '1';
    const iframeUrl = new URL('/mini-socket', signalingUrl);
    iframeUrl.searchParams.set('logLevel', logLevel);
    const iframeSrc = iframeUrl.toString();

    const iframe = document.createElement('iframe');
    iframe.id = RTC_IFRAME_ID;
    iframe.src = iframeSrc;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    iframeRef.current = iframe;

    return () => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current);
      }
    };
  }, [signalingUrl, debug, hasSignalingUrl]);

  return null;
};

export default MiniSocketListener;
